// src/pages/ResetPassword.jsx
import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("checking"); // checking | ready | saving | done | error
  const [error, setError] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw1, setShowPw1] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const didInit = useRef(false);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    (async () => {
      try {
        await bootstrapRecoverySession();
        setPhase("ready");
      } catch (e) {
        setError(e?.message || "Could not initialize password reset.");
        setPhase("error");
      }
    })();
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    const a = (pw1 || "").trim();
    const b = (pw2 || "").trim();

    if (a.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (a !== b) {
      setError("Passwords do not match.");
      return;
    }

    setPhase("saving");

    try {
      const { error } = await supabase.auth.updateUser({ password: a });
      if (error) throw error;

      // Optional: end the short-lived recovery session for safety
      try {
        await supabase.auth.signOut();
      } catch {}

      setPhase("done");
      setTimeout(() => navigate("/login", { replace: true }), 600);
    } catch (e) {
      setError(e?.message || "Could not update password.");
      setPhase("ready");
    }
  }

  return (
    <div className="min-h-screen bg-[#FFF7ED] font-sans flex flex-col">
      <header
        className="bg-[#FF914D] text-white"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="px-4 pt-2 pb-2 flex items-center justify-between">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-3 py-1 rounded focus:outline-none focus:ring-2 focus:ring-white/60 hover:opacity-90"
            aria-label="Back to Login"
          >
            <span aria-hidden="true" className="text-2xl leading-none">
              ←
            </span>
            <span className="hidden sm:inline text-base">Back</span>
          </Link>

          <img
            src="https://tknzgegeniiojzsascfl.supabase.co/storage/v1/object/public/pictures/Icon2.png"
            alt="Start logo"
            className="h-[60px] w-auto object-contain drop-shadow-sm select-none"
            loading="eager"
            decoding="async"
            draggable="false"
          />

          <span className="w-[52px]" aria-hidden="true" />
        </div>

        <div className="text-center pb-6">
          <h2 className="text-[32px] md:text-[36px] font-sans font-medium">
            Reset Password
          </h2>
        </div>
      </header>

      <main className="flex-1 px-6 py-8 max-w-md mx-auto w-full">
        {phase === "checking" && (
          <p className="text-center text-[#757575]">Preparing your reset…</p>
        )}

        {(phase === "ready" || phase === "saving") && (
          <form
            onSubmit={handleSubmit}
            className="space-y-6 rounded-3xl border border-[#FFE1CF] bg-white p-6 shadow-sm"
          >
            {error && (
              <p className="rounded-2xl bg-red-50 px-4 py-3 text-red-600 text-sm text-center">
                {error}
              </p>
            )}

            {/* New password */}
            <div className="space-y-3">
              <label className="block text-[#333333] text-lg font-sans font-bold">
                New password
              </label>

              <div className="relative">
                <input
                  type={showPw1 ? "text" : "password"}
                  value={pw1}
                  onChange={(e) => setPw1(e.target.value)}
                  className="w-full bg-transparent border-b border-[#D8D8D8] pb-2 pr-10 text-[#333333] placeholder-[#9A9A9A] font-sans text-sm focus:border-[#FF914D] focus:outline-none"
                  placeholder="Enter new password"
                  autoComplete="new-password"
                  disabled={phase === "saving"}
                />

                <button
                  type="button"
                  onClick={() => setShowPw1((s) => !s)}
                  aria-label={showPw1 ? "Hide password" : "Show password"}
                  aria-pressed={showPw1}
                  className="absolute right-0 top-1/2 -translate-y-1/2 p-1 text-[#757575] hover:text-[#FF914D] focus:outline-none"
                >
                  {showPw1 ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4.5-9-7 0-.64.21-1.31.6-2 .26-.47.6-.96 1.02-1.45M6.1 6.1C7.88 4.99 9.88 4 12 4c5 0 9 4.5 9 7 0 1.02-.47 2.16-1.3 3.27M3 3l18 18M9.88 9.88A3 3 0 0012 15a3 3 0 002.12-.88"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                      <circle cx="12" cy="12" r="3" strokeWidth="2" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Confirm password */}
            <div className="space-y-3">
              <label className="block text-[#333333] text-lg font-sans font-bold">
                Confirm password
              </label>

              <div className="relative">
                <input
                  type={showPw2 ? "text" : "password"}
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  className="w-full bg-transparent border-b border-[#D8D8D8] pb-2 pr-10 text-[#333333] placeholder-[#9A9A9A] font-sans text-sm focus:border-[#FF914D] focus:outline-none"
                  placeholder="Re-type new password"
                  autoComplete="new-password"
                  disabled={phase === "saving"}
                />

                <button
                  type="button"
                  onClick={() => setShowPw2((s) => !s)}
                  aria-label={showPw2 ? "Hide password" : "Show password"}
                  aria-pressed={showPw2}
                  className="absolute right-0 top-1/2 -translate-y-1/2 p-1 text-[#757575] hover:text-[#FF914D] focus:outline-none"
                >
                  {showPw2 ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4.5-9-7 0-.64.21-1.31.6-2 .26-.47.6-.96 1.02-1.45M6.1 6.1C7.88 4.99 9.88 4 12 4c5 0 9 4.5 9 7 0 1.02-.47 2.16-1.3 3.27M3 3l18 18M9.88 9.88A3 3 0 0012 15a3 3 0 002.12-.88"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                      <circle cx="12" cy="12" r="3" strokeWidth="2" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={phase === "saving"}
              className="w-full bg-[#FF914D] text-white font-sans text-lg py-3 rounded-[25px] hover:bg-[#E9782F] transition-colors disabled:opacity-50"
            >
              {phase === "saving" ? "Updating…" : "Update password"}
            </button>

            <div className="text-center text-xs opacity-90">
              <Link
                to="/login"
                className="font-semibold text-[#FF914D] hover:text-[#E9782F] hover:underline"
              >
                Back to login
              </Link>
            </div>
          </form>
        )}

        {phase === "done" && (
          <div className="rounded-3xl border border-[#FFE1CF] p-6 shadow-sm bg-white text-center">
            <h3 className="text-lg font-bold text-[#333333]">
              Password updated ✅
            </h3>
            <p className="mt-2 text-sm text-[#757575]">
              Redirecting you to login…
            </p>
            <div className="mt-4">
              <Link
                to="/login"
                className="inline-block px-5 py-2 rounded-full bg-[#FF914D] text-white font-medium hover:bg-[#E9782F]"
              >
                Go to Login
              </Link>
            </div>
          </div>
        )}

        {phase === "error" && (
          <div className="rounded-3xl border border-red-200 p-6 shadow-sm bg-white text-center">
            <h3 className="text-lg font-bold text-red-700">
              We couldn’t start the reset
            </h3>
            <p className="mt-2 text-sm text-[#757575]">{error}</p>
            <div className="mt-4">
              <Link
                to="/forgot-password"
                className="inline-block px-5 py-2 rounded-full bg-[#FF914D] text-white font-medium hover:bg-[#E9782F]"
              >
                Try again
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

/**
 * Handles all Supabase email link styles:
 * - Fragment tokens:  #access_token=...&refresh_token=...&type=recovery
 * - Auth code:        ?code=...
 * - Token hash:       ?token_hash=...&type=recovery
 */
async function bootstrapRecoverySession() {
  const url = new URL(window.location.href);

  // 1) Hash/fragment tokens common for magic links: #access_token=...&refresh_token=...
  if (url.hash && url.hash.includes("access_token")) {
    const frag = new URLSearchParams(url.hash.slice(1));
    const access_token = frag.get("access_token");
    const refresh_token = frag.get("refresh_token");

    if (!access_token || !refresh_token) {
      throw new Error("Invalid recovery link.");
    }

    const { error } = await supabase.auth.setSession({
      access_token,
      refresh_token,
    });

    if (error) throw error;

    stripHashAndQuery();
    return;
  }

  // 2) PKCE OAuth/verify-style code: ?code=...
  const code = url.searchParams.get("code");

  if (code) {
    const { error } = await supabase.auth.exchangeCodeForSession(code);

    if (error) throw error;

    stripHashAndQuery();
    return;
  }

  // 3) Token hash flow: ?type=recovery&token_hash=...
  const tokenHash = url.searchParams.get("token_hash");
  const type = url.searchParams.get("type");

  if (tokenHash && type === "recovery") {
    const { error } = await supabase.auth.verifyOtp({
      type: "recovery",
      token_hash: tokenHash,
    });

    if (error) throw error;

    stripHashAndQuery();
    return;
  }

  // If none of the above are present, we simply proceed. User might already be authed.
}

function stripHashAndQuery() {
  try {
    const { origin, pathname } = new URL(window.location.href);
    window.history.replaceState({}, document.title, `${origin}${pathname}`);
  } catch {}
}